import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useStores } from '@/hooks';
import { AppInput, AppButton, AppTextarea, AppAlert, AppSingleSelect, AppMultiSelect, AppDatePicker, AppAutocomplete, AppTimePicker } from '@pro-sto/ui';
import type { SelectOption } from '@pro-sto/ui';
import type { SelectOption as MultiSelectOption } from '@pro-sto/ui';
import type { SelectOption as AutocompleteOption } from '@pro-sto/ui';
import type { ClientSearchResultDto, CarSearchResultDto } from '@pro-sto/api-client';
import { 
  carsControllerGetMakes,
  carsControllerGetModelsByMakeId,
  adminFindOrCreateClient,
  adminCreateOrUpdateCar,
  adminCreateBooking,
  serviceCenterGetSlots,
  adminSearchClients,
  adminSearchCars,
  adminGetServiceCenterPosts,
} from '@pro-sto/api-client';
import type { ServiceCenterPostResponseDto } from '@pro-sto/api-client';
import { getWorkingHoursForDate } from '@/utils/scheduleHelpers';
import { getApiErrorMessage } from '@/utils/errors';
import { normalizePhoneToE164, isPhoneComplete, findClientByExactPhone, resolveCanAdminUpdateClientName } from '@/features/clients';
import { toAdditionalServiceUuids } from '@/features/bookings';
import './MobileCreateBooking.css';

// Иконка часов для поля времени
const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M10 5V10L13 13M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" 
      stroke="#B2B1AE" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

interface LocationState {
  initialDate?: Date;
  initialTime?: string;
}

interface CarMake {
  id?: string;
  name?: string;
}

interface CarModel {
  id?: string;
  name?: string;
  markId?: string;
}

export const MobileCreateBooking = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { servicesStore, toastStore, authStore, operatingHoursStore, bookingsStore } = useStores();
  
  const locationState = location.state as LocationState | null;
  
  // Получаем параметры из URL query
  const searchParams = new URLSearchParams(location.search);
  const dateParam = searchParams.get('date'); // format: 'yyyy-MM-dd'
  const timeParam = searchParams.get('time'); // format: 'HH:mm'
  
  // Определяем начальную дату и время из URL параметров или location state
  const getInitialDate = (): Date | null => {
    if (dateParam) {
      return new Date(dateParam);
    }
    if (locationState?.initialDate) {
      return new Date(locationState.initialDate);
    }
    return null;
  };
  
  const getInitialTime = (): string => {
    if (timeParam) {
      return timeParam;
    }
    if (locationState?.initialTime) {
      return locationState.initialTime;
    }
    return '';
  };

  // Form state
  const [phone, setPhone] = useState(''); // Храним введенный телефон
  const [licensePlate, setLicensePlate] = useState('');
  const [selectedMake, setSelectedMake] = useState<SelectOption | null>(null);
  const [selectedModel, setSelectedModel] = useState<SelectOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(getInitialDate());
  const [selectedTime, setSelectedTime] = useState(getInitialTime());
  const [selectedService, setSelectedService] = useState<SelectOption | null>(null);
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState<MultiSelectOption[]>([]);
  const [comment, setComment] = useState('');
  const [timeError, setTimeError] = useState<string>('');
  
  // Client and car autocomplete state
  const [selectedClient, setSelectedClient] = useState<ClientSearchResultDto | null>(null);
  const [clientName, setClientName] = useState<string>('');
  const [selectedCar, setSelectedCar] = useState<CarSearchResultDto | null>(null);
  const [phoneAutocompleteValue, setPhoneAutocompleteValue] = useState<AutocompleteOption | undefined>();
  const [carAutocompleteValue, setCarAutocompleteValue] = useState<AutocompleteOption | undefined>();
  const [clientCarsOptions, setClientCarsOptions] = useState<AutocompleteOption[]>([]); // Опции автомобилей выбранного клиента
  
  // Data state
  const [makes, setMakes] = useState<CarMake[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  // Available time slots state
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  
  // Posts state
  const [posts, setPosts] = useState<ServiceCenterPostResponseDto[]>([]);
  const [selectedPost, setSelectedPost] = useState<SelectOption | null>(null);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  
  // Сохраняем оригинальные значения марки/модели при автозаполнении (для визуальной индикации)
  const originalCarDetails = useRef<{ make: string | null; model: string | null }>({ make: null, model: null });
  
  // Флаг для однократного автовыбора услуги
  const hasAutoSelectedService = useRef(false);
  const isFirstLoad = useRef(true);
  
  // Ref для хранения имени, введенного пользователем вручную
  const userEnteredName = useRef<string>('');
  
  // Состояние для показа алерта о конфликте имен
  const [nameConflict, setNameConflict] = useState<{ dbName: string; enteredName: string } | null>(null);
  
  // Ref для хранения марки и модели, введенных пользователем вручную
  const userEnteredCarDetails = useRef<{ make: string | null; model: string | null }>({ make: null, model: null });
  
  // Состояние для показа алерта о конфликте марки/модели
  const [carConflict, setCarConflict] = useState<{ dbMake: string; dbModel: string; enteredMake: string; enteredModel: string } | null>(null);

  // Load makes on mount
  useEffect(() => {
    const loadMakes = async () => {
      try {
        const response = await carsControllerGetMakes({ limit: 1000 });
        setMakes(response.data || []);
      } catch (error) {
        console.error('Failed to load car makes:', error);
        toastStore.showError('Не удалось загрузить марки автомобилей');
      }
    };

    loadMakes();
  }, [toastStore]);
  
  // Load posts on mount
  useEffect(() => {
    const loadPosts = async () => {
      if (!authStore.user?.service_center_uuid) return;
      
      setIsLoadingPosts(true);
      try {
        const response = await adminGetServiceCenterPosts({
          serviceCenterUuid: authStore.user.service_center_uuid,
        });
        const postsData = response.data || [];
        setPosts(postsData);
        
        // Auto-select first post
        if (postsData.length > 0) {
          setSelectedPost({
            label: `Пост ${postsData[0].orderNumber}`,
            value: postsData[0].uuid,
          });
        }
      } catch (error) {
        console.error('Failed to load posts:', error);
        toastStore.showError('Не удалось загрузить посты');
      } finally {
        setIsLoadingPosts(false);
      }
    };

    loadPosts();
  }, [authStore.user?.service_center_uuid, toastStore]);

  // Load models when make is selected
  useEffect(() => {
    const loadModels = async (makeId: string) => {
      setIsLoadingModels(true);
      try {
        const response = await carsControllerGetModelsByMakeId({ id: makeId, limit: 1000 });
        setModels(response.data || []);
      } catch (error) {
        console.error('Failed to load car models:', error);
        toastStore.showError('Не удалось загрузить модели автомобилей');
      } finally {
        setIsLoadingModels(false);
      }
    };

    if (selectedMake) {
      loadModels(selectedMake.value!.toString());
      // Сбрасываем модель при смене марки
      setSelectedModel(null);
    } else {
      setModels([]);
      setSelectedModel(null);
    }
  }, [selectedMake, toastStore]);

  // Load services on mount
  useEffect(() => {
    if (servicesStore.services.length === 0) {
      servicesStore.fetchServices();
    }
  }, [servicesStore]);

  // Auto-select first main service when services are loaded (only once on mount)
  useEffect(() => {
    if (servicesStore.mainServices.length > 0 && !selectedService && !hasAutoSelectedService.current) {
      const firstService = servicesStore.mainServices[0];
      setSelectedService({
        label: firstService.name,
        value: firstService.uuid,
      });
      hasAutoSelectedService.current = true;
    }
  }, [servicesStore.mainServices, selectedService]);

  // Load available slots when date or service changes
  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedDate || !selectedService || !authStore.user?.service_center_uuid) {
        setAvailableTimeSlots([]);
        setIsLoadingSlots(false);
        return;
      }

      setIsLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await serviceCenterGetSlots({
          uuid: authStore.user.service_center_uuid,
          serviceUuid: selectedService.value!.toString(),
          date: dateStr,
        });

        // Преобразуем ISO даты, округляем вверх до целого часа и убираем дубликаты
        const slotsSet = new Set<string>();
        response.forEach((timeSlot: string) => {
          const slotDate = new Date(timeSlot);
          let hours = slotDate.getHours();
          const minutes = slotDate.getMinutes();
          
          // Если минуты не 0, округляем вверх до следующего часа
          if (minutes > 0) {
            hours += 1;
          }
          
          // Добавляем только если час в пределах суток
          if (hours < 24) {
            const timeString = `${hours.toString().padStart(2, '0')}:00`;
            slotsSet.add(timeString);
          }
        });
        
        const slots = Array.from(slotsSet).sort();

        setAvailableTimeSlots(slots);

        if (!isFirstLoad.current) {
          setSelectedTime((current) =>
            current && slots.includes(current) ? current : '',
          );
        } else {
          isFirstLoad.current = false;
        }
      } catch (error) {
        console.error('Failed to load available slots:', error);
        toastStore.showError('Не удалось загрузить доступные слоты');
        setAvailableTimeSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadSlots();
  }, [selectedDate, selectedService, authStore.user, toastStore]);

  const makeOptions: SelectOption[] = makes
    .filter(make => make.id && make.name)
    .map(make => ({
      label: make.name!,
      value: make.id!,
    }));

  const modelOptions: SelectOption[] = models
    .filter(model => model.id && model.name)
    .map(model => ({
      label: model.name!,
      value: model.id!,
    }));

  const mainServiceOptions: SelectOption[] = servicesStore.mainServices
    .map(service => ({
      label: service.name,
      value: service.uuid,
    }));

  const additionalServiceOptions: MultiSelectOption[] = servicesStore.additionalServices
    .map(service => ({
      label: service.name,
      value: service.uuid,
    }));

  // Функция поиска клиентов по номеру телефона
  const searchClients = useCallback(async (phoneQuery: string): Promise<AutocompleteOption[]> => {
    const digits = phoneQuery.replace(/\D/g, '');
    const searchDigits = digits.startsWith('7') ? digits.substring(1) : digits;
    
    if (searchDigits.length < 3) {
      return [];
    }

    try {
      const results = await adminSearchClients({ phone: searchDigits, limit: 10 });
      
      return results.map((client: ClientSearchResultDto) => ({
        label: client.phone,
        value: client.uuid,
        isCustom: false,
        rawData: client,
      } as AutocompleteOption & { rawData: ClientSearchResultDto }));
    } catch (error) {
      console.error('Failed to search clients:', error);
      toastStore.showError('Не удалось выполнить поиск клиентов');
      return [];
    }
  }, [toastStore]);

  // Обработчик выбора клиента из автокомплита
  const handleClientSelect = useCallback(async (option: AutocompleteOption) => {
    setPhoneAutocompleteValue(option);
    setPhoneError('');
    
    if (option.isCustom || !option.value) {
      setSelectedClient(null);
      // НЕ сбрасываем clientName - сохраняем введенное пользователем имя
      setClientCarsOptions([]);
      setPhone(option.label);
      return;
    }

    // Ищем клиента в результатах поиска
    try {
      const phoneDigits = option.label.replace(/\D/g, '');
      const searchDigits = phoneDigits.startsWith('7') ? phoneDigits.substring(1) : phoneDigits;
      const clients = await adminSearchClients({ phone: searchDigits, limit: 1 });
      
      if (clients.length > 0) {
        const clientData = clients[0];
        const dbName = clientData.name ? String(clientData.name) : '';
        const enteredName = userEnteredName.current.trim();
        
        // Проверяем, если пользователь ввел имя и оно отличается от имени в БД
        if (enteredName && dbName && enteredName.toLowerCase() !== dbName.toLowerCase()) {
          // Показываем алерт о конфликте имен
          setNameConflict({ dbName, enteredName });
        } else {
          // Если пользователь не вводил имя или имена совпадают - используем имя из БД
          setClientName(dbName);
        }
        
        setSelectedClient(clientData);
        setPhone(clientData.phone);
        
        // Загружаем автомобили выбранного клиента
        const cars = await adminSearchCars({
          licensePlate: '',
          clientUuid: clientData.uuid,
          limit: 50,
        });
        
        const carsOptions: AutocompleteOption[] = cars.map((car: CarSearchResultDto) => ({
          label: car.license_plate,
          value: car.uuid,
          isCustom: false,
          rawData: car,
        } as AutocompleteOption & { rawData: CarSearchResultDto }));
        
        setClientCarsOptions(carsOptions);
      }
    } catch (error) {
      console.error('Failed to load client data:', error);
      setClientCarsOptions([]);
    }
  }, []);

  // Обработчик замены имени на имя из базы
  const handleReplaceNameWithDb = useCallback(() => {
    if (nameConflict) {
      setClientName(nameConflict.dbName);
      setNameConflict(null);
    }
  }, [nameConflict]);

  // Обработчик сохранения введенного имени
  const handleKeepEnteredName = useCallback(() => {
    setNameConflict(null);
  }, []);

  // Обработчик замены марки/модели на данные из базы
  const handleReplaceCarWithDb = useCallback(() => {
    if (carConflict) {
      const dbMakeOption = makeOptions.find(opt => opt.label === carConflict.dbMake);
      const dbModelOption = modelOptions.find(opt => opt.label === carConflict.dbModel);
      
      if (dbMakeOption) {
        setSelectedMake(dbMakeOption);
        userEnteredCarDetails.current.make = carConflict.dbMake;
      }
      if (dbModelOption) {
        setSelectedModel(dbModelOption);
        userEnteredCarDetails.current.model = carConflict.dbModel;
      }
      
      setCarConflict(null);
    }
  }, [carConflict, makeOptions, modelOptions]);

  // Обработчик сохранения введенных марки/модели
  const handleKeepEnteredCar = useCallback(() => {
    setCarConflict(null);
  }, []);

  // Функция поиска автомобилей по номеру
  const searchCars = useCallback(async (plateQuery: string): Promise<AutocompleteOption[]> => {
    if (plateQuery.length < 2) {
      return [];
    }

    try {
      const results = await adminSearchCars({
        licensePlate: plateQuery,
        clientUuid: selectedClient?.uuid,
        limit: 10,
      });
      
      return results.map((car: CarSearchResultDto) => ({
        label: car.license_plate,
        value: car.uuid,
        isCustom: false,
        rawData: car,
      } as AutocompleteOption & { rawData: CarSearchResultDto }));
    } catch (error) {
      console.error('Failed to search cars:', error);
      toastStore.showError('Не удалось выполнить поиск автомобилей');
      return [];
    }
  }, [selectedClient?.uuid, toastStore]);

  // Обработчик выбора автомобиля из автокомплита
  const handleCarSelect = useCallback(async (option: AutocompleteOption) => {
    setCarAutocompleteValue(option);
    
    if (option.isCustom || !option.value) {
      setSelectedCar(null);
      setLicensePlate(option.label);
      return;
    }

    // Ищем автомобиль в результатах поиска
    try {
      const cars = await adminSearchCars({
        licensePlate: option.label,
        clientUuid: selectedClient?.uuid,
        limit: 1,
      });
      
      if (cars.length > 0) {
        const carData = cars[0];
        
        // Проверяем, есть ли конфликт между введенными и найденными данными
        const enteredMake = userEnteredCarDetails.current.make;
        const enteredModel = userEnteredCarDetails.current.model;
        
        const hasMakeConflict = enteredMake && enteredMake !== carData.make;
        const hasModelConflict = enteredModel && enteredModel !== carData.model;
        
        if ((hasMakeConflict || hasModelConflict) && enteredMake && enteredModel) {
          // Показываем alert о конфликте
          setCarConflict({
            dbMake: carData.make,
            dbModel: carData.model,
            enteredMake: enteredMake,
            enteredModel: enteredModel
          });
          
          // Устанавливаем только данные машины и номер
          setSelectedCar(carData);
          setLicensePlate(carData.license_plate);
          
          // Сохраняем оригинальные данные
          originalCarDetails.current = {
            make: carData.make,
            model: carData.model
          };
          
          return;
        }
        
        setSelectedCar(carData);
        setLicensePlate(carData.license_plate);
        
        // Сохраняем оригинальные значения для справки
        originalCarDetails.current = {
          make: carData.make,
          model: carData.model,
        };
        
        const makeOption = makeOptions.find(m => m.label === carData.make);
        if (makeOption) {
          setSelectedMake(makeOption);
          userEnteredCarDetails.current.make = carData.make;
        }
      }
    } catch (error) {
      console.error('Failed to load car data:', error);
    }
  }, [selectedClient?.uuid, makeOptions]);

  // Предзаполнение модели при выборе автомобиля
  useEffect(() => {
    if (selectedCar && models.length > 0 && selectedMake) {
      // Проверяем, что выбранная марка соответствует марке автомобиля
      if (selectedMake.label === selectedCar.make) {
        const modelOption = models
          .filter(m => m.id && m.name && m.markId === selectedMake.value)
          .map(m => ({ label: m.name!, value: m.id! }))
          .find(m => m.label === selectedCar.model);
        
        if (modelOption) {
          setSelectedModel(modelOption);
          userEnteredCarDetails.current.model = modelOption.label;
        }
      }
    }
  }, [selectedCar, models, selectedMake]);

  // Вычисление общей стоимости
  const totalCost = useMemo(() => {
    let cost = 0;
    
    if (selectedService) {
      const service = servicesStore.services.find(s => s.uuid === selectedService.value);
      if (service) {
        cost += service.price || 0;
      }
    }
    
    if (selectedAdditionalServices.length > 0) {
      selectedAdditionalServices.forEach(additionalService => {
        const service = servicesStore.services.find(s => s.uuid === additionalService.value);
        if (service) {
          cost += service.price || 0;
        }
      });
    }
    
    return cost;
  }, [selectedService, selectedAdditionalServices, servicesStore.services]);

  // Обработчик изменения времени с очисткой ошибки
  const handleTimeChange = useCallback((newTime: string) => {
    setSelectedTime(newTime);
    if (timeError) {
      setTimeError('');
    }
  }, [timeError]);

  // Обработчик изменения даты с проверкой доступности текущего времени
  const handleDateChange = useCallback((newDate: Date | null) => {
    setSelectedDate(newDate);
    if (timeError) {
      setTimeError('');
    }
    
    // Если есть выбранное время, проверяем его доступность для новой даты
    if (newDate && selectedTime) {
      const workingHours = getWorkingHoursForDate(
        newDate,
        operatingHoursStore.regularSchedule,
        operatingHoursStore.specialDates
      );
      
      // Если дата - выходной или нерабочий день (workingHours === null), сбрасываем время
      if (!workingHours) {
        setSelectedTime('');
        return;
      }
      
      // Проверяем, доступно ли выбранное время в рабочих часах новой даты
      if (selectedTime < workingHours.open || selectedTime >= workingHours.close) {
        // Если время не входит в рабочие часы, сбрасываем его
        setSelectedTime('');
      }
      // Иначе оставляем выбранное время как есть
    }
  }, [timeError, selectedTime, operatingHoursStore.regularSchedule, operatingHoursStore.specialDates]);

  const handleBack = () => {
    // Очищаем состояния конфликтов
    userEnteredName.current = '';
    setNameConflict(null);
    userEnteredCarDetails.current = { make: null, model: null };
    setCarConflict(null);
    
    navigate('/orders');
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedDate) {
      toastStore.showError('Выберите дату');
      return;
    }

    if (!selectedTime) {
      toastStore.showError('Выберите время');
      return;
    }

    // Проверка что выбранная дата и время не в прошлом
    const [checkHours, checkMinutes] = selectedTime.split(':').map(Number);
    const bookingDateTime = new Date(selectedDate);
    bookingDateTime.setHours(checkHours, checkMinutes, 0, 0);
    if (bookingDateTime < new Date()) {
      toastStore.showError('Нельзя создать запись на прошедшее время');
      return;
    }

    // Проверка доступности времени согласно режиму работы
    const workingHours = getWorkingHoursForDate(
      selectedDate,
      operatingHoursStore.regularSchedule,
      operatingHoursStore.specialDates
    );

    if (!workingHours) {
      setTimeError('В этот день сервисный центр не работает');
      toastStore.showError('В этот день сервисный центр не работает');
      return;
    }

    // Проверяем, что время находится в рабочих часах
    if (selectedTime < workingHours.open || selectedTime >= workingHours.close) {
      setTimeError(`Доступное время: ${workingHours.open}-${workingHours.close}`);
      toastStore.showError(`Недоступное время. Рабочие часы: ${workingHours.open}-${workingHours.close}`);
      return;
    }

    if (
      availableTimeSlots.length > 0 &&
      !availableTimeSlots.includes(selectedTime) &&
      !availableTimeSlots.includes(`${selectedTime.split(':')[0]}:00`)
    ) {
      setTimeError('Выбранное время недоступно для этой услуги');
      toastStore.showError('Выбранное время недоступно для этой услуги');
      return;
    }

    if (!selectedService) {
      toastStore.showError('Выберите услугу');
      return;
    }

    if (!selectedPost) {
      toastStore.showError('Выберите пост');
      return;
    }

    if (!authStore.user?.service_center_uuid) {
      toastStore.showError('Не найден UUID сервисного центра');
      return;
    }

    if (!isPhoneComplete(phone)) {
      setPhoneError('Введите номер телефона полностью');
      toastStore.showError('Введите номер телефона полностью');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create/find client
      const cleanPhone = normalizePhoneToE164(phone);
      if (!cleanPhone) {
        setIsSubmitting(false);
        toastStore.showError('Неверный формат телефона');
        return;
      }
      
      const trimmedName = clientName.trim();
      let existingClient = selectedClient;
      try {
        existingClient = selectedClient ?? (await findClientByExactPhone(phone));
      } catch {
        existingClient = selectedClient;
      }
      const canUpdateName = await resolveCanAdminUpdateClientName({
        clientUuid: existingClient?.uuid,
        serviceCenterUuid: authStore.user.service_center_uuid,
        localBookings: [...bookingsStore.bookings, ...bookingsStore.pendingBookings],
      });
      const clientResponse = await adminFindOrCreateClient({
        requestBody: {
          phone: cleanPhone,
          ...(trimmedName && canUpdateName ? { name: trimmedName } : {}),
        },
      });

      if (trimmedName && canUpdateName) {
        bookingsStore.setClientDisplayName(clientResponse.uuid, trimmedName);
      }

      // 2. Create/update car
      // Если данные автомобиля не указаны, используем дефолтные значения
      const carLicensePlate = licensePlate.trim() || 'Не указан';
      const carMake = selectedMake?.label || 'Не указана';
      const carModel = selectedModel?.label || 'Не указана';
      const carMakeId = selectedMake?.value ? String(selectedMake.value) : undefined;
      const carModelId = selectedModel?.value ? String(selectedModel.value) : undefined;
      
      const carResponse = await adminCreateOrUpdateCar({
        clientUuid: clientResponse.uuid,
        requestBody: {
          license_plate: carLicensePlate,
          make: carMake,
          model: carModel,
          make_id: carMakeId,
          model_id: carModelId,
        }
      });

      // 3. Create booking
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const startTime = new Date(selectedDate);
      startTime.setHours(hours, minutes, 0, 0);

      const additionalServiceUuids = toAdditionalServiceUuids(
        selectedAdditionalServices,
        servicesStore.additionalServices,
      );

      await adminCreateBooking({
        requestBody: {
          service_center_uuid: authStore.user.service_center_uuid,
          client_uuid: clientResponse.uuid,
          car_uuid: carResponse.uuid,
          service_uuid: String(selectedService.value),
          post_uuid: String(selectedPost!.value),
          start_time: startTime.toISOString(),
          payment_method: 'cash',
          additional_service_uuids: additionalServiceUuids,
          admin_comment: comment || undefined,
        }
      });

      toastStore.showSuccess('Запись успешно создана');
      
      // Navigate back to orders page
      navigate('/orders');
    } catch (error: unknown) {
      console.error('Failed to create booking:', error);
      
      const apiError = error as { status?: number };
      if (apiError?.status === 409) {
        toastStore.showError('Временной слот уже занят');
      } else if (apiError?.status === 404) {
        toastStore.showError(getApiErrorMessage(error, 'Не найдены необходимые данные'));
      } else if (apiError?.status === 400) {
        toastStore.showError(getApiErrorMessage(error, 'Некорректные данные запроса'));
      } else {
        toastStore.showError('Не удалось создать запись');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mobile-create-booking" data-testid="mobile-create-booking">
      <div className="mobile-create-booking__header">
        <button 
          className="mobile-create-booking__back"
          onClick={handleBack}
          aria-label="Назад"
          data-testid="mobile-create-booking-back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="mobile-create-booking__title">Добавление записи</h1>
      </div>

      <div className="mobile-create-booking__content">
        <div className="mobile-create-booking__field">
          <AppAutocomplete
            label="Номер телефона"
            placeholder="+7"
            value={phoneAutocompleteValue}
            onSearch={searchClients}
            onChange={handleClientSelect}
            minSearchLength={3}
            searchDebounce={300}
            required
            error={phoneError}
            renderOption={(option) => {
              const clientData = (option as AutocompleteOption & { rawData?: ClientSearchResultDto }).rawData;
              if (clientData?.name) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', pointerEvents: 'none' }}>
                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{clientData.name}</div>
                  </div>
                );
              }
              return option.label;
            }}
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppInput
            label="Имя клиента"
            placeholder="Имя"
            value={clientName}
            onChange={(value) => {
              setClientName(value);
              // Сохраняем введенное пользователем имя
              userEnteredName.current = value;
            }}
          />
        </div>

        {/* Алерт о конфликте имен */}
        {nameConflict && (
          <div className="mobile-create-booking__field">
            <AppAlert
              message={`Мы нашли пользователя в базе с таким номером, но с другим именем. Заменить на "${nameConflict.dbName}"?`}
              onConfirm={handleReplaceNameWithDb}
              onCancel={handleKeepEnteredName}
              confirmText="Заменить"
              cancelText="Оставить"
            />
          </div>
        )}

        <div className="mobile-create-booking__field">
          <AppAutocomplete
            label="Введите номер"
            placeholder="A000AA 111"
            value={carAutocompleteValue}
            onSearch={searchCars}
            onChange={handleCarSelect}
            options={clientCarsOptions}
            minSearchLength={2}
            searchDebounce={300}
            renderOption={(option) => {
              const carData = (option as AutocompleteOption & { rawData?: CarSearchResultDto }).rawData;
              if (carData?.make && carData?.model) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', pointerEvents: 'none' }}>
                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{carData.make} {carData.model}</div>
                  </div>
                );
              }
              return option.label;
            }}
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppSingleSelect
            label="Выберите марку"
            placeholder="Марка"
            options={makeOptions}
            value={selectedMake}
            onChange={(value) => {
              setSelectedMake(value);
              if (value) {
                userEnteredCarDetails.current.make = value.label;
              }
            }}
            clearable
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppSingleSelect
            label="Выберите модель"
            placeholder="Модель"
            options={modelOptions}
            value={selectedModel}
            onChange={(value) => {
              setSelectedModel(value);
              if (value) {
                userEnteredCarDetails.current.model = value.label;
              }
            }}
            disabled={!selectedMake || isLoadingModels}
            clearable
          />
        </div>
        
        {carConflict && (
          <AppAlert
            message={`В базе найдена машина с другими данными: "${carConflict.dbMake} ${carConflict.dbModel}". Заменить на данные из базы?`}
            onConfirm={handleReplaceCarWithDb}
            confirmText="Заменить"
            onCancel={handleKeepEnteredCar}
            cancelText="Оставить введенные"
          />
        )}

        <div className="mobile-create-booking__date-time-section">
          <div className="mobile-create-booking__date-field">
            <AppDatePicker
              label="Дата"
              value={selectedDate}
              onChange={handleDateChange}
              minDate={new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </div>

          <div className="mobile-create-booking__time-field">
            <AppTimePicker
              label=" "
              value={selectedTime}
              onChange={handleTimeChange}
              placeholder="09:00"
              availableSlots={availableTimeSlots}
              disabled={isLoadingSlots || !selectedDate || !selectedService}
              iconLeft={<ClockIcon />}
              error={timeError}
            />
          </div>
        </div>

        <div className="mobile-create-booking__field">
          <AppSingleSelect
            label="Название услуги"
            placeholder="Выберите услугу"
            options={mainServiceOptions}
            value={selectedService}
            onChange={setSelectedService}
            clearable
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppSingleSelect
            label="Пост"
            placeholder="Выберите пост"
            options={posts.map(post => ({
              label: `Пост ${post.orderNumber}`,
              value: post.uuid,
            }))}
            value={selectedPost}
            onChange={setSelectedPost}
            disabled={isLoadingPosts}
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppMultiSelect
            label="Дополнительные услуги"
            placeholder="Выберите дополнительные услуги"
            options={additionalServiceOptions}
            value={selectedAdditionalServices}
            onChange={setSelectedAdditionalServices}
            multiline
          />
        </div>

        <div className="mobile-create-booking__field">
          <AppTextarea
            label="Комментарий к заказу"
            placeholder="Введите комментарий"
            value={comment}
            onChange={setComment}
            minRows={2}
            maxRows={10}
            autoResize
          />
        </div>
      </div>

      <div className="mobile-create-booking__footer">
        <AppButton
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
          data-testid="mobile-create-booking-submit"
        >
          {totalCost > 0 ? `Создать заказ ${totalCost}₽` : 'Создать заказ'}
        </AppButton>
      </div>
    </div>
  );
});
